// The browser side of Otto Live Protocol v1 (@otto/protocol). One WebSocket to
// otto-live per consult: binary PCM16 audio both ways and small JSON events.
// No React and no audio here, so every rule runs in tests with a fake socket.
//
// Rules this client owns (design 4.3, 4.4, 9.4):
//   - `hello` first on a fresh call; `resume` (with the measured gap and the last
//     Otto turn the browser finished playing) on every reconnect.
//   - Otto's audio belongs to the turn announced by `otto_speaking`; after
//     `interrupted` or `otto_done`, frames are dropped until the next
//     `otto_speaking` (WebSocket order makes this exact).
//   - A browser-side drop reconnects at 0.5, 1, 2, 4 s (then every 4 s) while
//     the server holds the session (60 s); after that it stops and waits for a
//     tap, because audio usually needs a gesture after a long drop anyway.
//   - Mic audio from a gap is never queued or replayed: answering stale speech
//     is worse than asking again. A socket that is backing up drops frames.
//   - The server always has the last word (`ended`); a non-retryable `error`
//     stops the client. Neither reconnects.
//   - A socket that says OPEN but has been silent past the watchdog (the server
//     pushes `state` at least every 5 s) is dropped and reconnected.
import {
  liveProtocols,
  PROTOCOL_VERSION,
  encodeEvent,
  isAcceptableMicMessage,
  parseServerEvent,
  type ClientEndReason,
  type ClientEvent,
  type ClientInfo,
  type EndReason,
  type LiveErrorCode,
  type Mode,
  type ServerEvent,
} from "@otto/protocol";

export type LiveStatus =
  | { kind: "idle" }
  | { kind: "connecting"; attempt: number; reconnect: boolean }
  | { kind: "live"; resumed: boolean }
  | { kind: "reconnecting"; attempt: number; nextInMs: number; downSince: number }
  | { kind: "lost"; downSince: number }
  | { kind: "ended"; reason: EndReason }
  | { kind: "failed"; code: LiveErrorCode | "socket"; message: string };

/** The parts of the browser WebSocket this client uses (a fake implements it in tests). */
export interface WebSocketLike {
  binaryType: string;
  readonly readyState: number;
  readonly bufferedAmount: number;
  send(data: string | ArrayBuffer): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

/** Opens a socket offering these subprotocols (the protocol version first, then the signed token). */
export type WebSocketFactory = (url: string, protocols: string[]) => WebSocketLike;

export interface LiveClientHandlers {
  onStatus(status: LiveStatus): void;
  /** Every valid server event, in order. */
  onEvent(event: ServerEvent): void;
  /** Otto's audio for `turn`, already filtered by the turn rules above. */
  onAudio(chunk: ArrayBuffer, turn: number): void;
  /** Round trip from ping to pong, ms. */
  onRtt?(ms: number): void;
  /** A server text message that failed validation. Loud in the console, never shown. */
  onProtocolError?(detail: string): void;
}

export interface LiveClientOptions {
  url: string;
  consultId: string;
  liveToken: string;
  mode: Mode;
  client?: ClientInfo;
  /** Start with `resume` instead of `hello` (a reload of a call already under way). */
  resumeFrom?: { gapMs: number } | null;
  /** The last Otto turn the browser finished playing, read at each resume. */
  lastTurnPlayed: () => number | null;
  handlers: LiveClientHandlers;
  createSocket?: WebSocketFactory;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (handle: unknown) => void;
  backoffMs?: readonly number[];
  holdWindowMs?: number;
  watchdogMs?: number;
  pingEveryMs?: number;
  /** Past this many unsent bytes, mic frames are dropped instead of queued (about 1 s of mic). */
  maxBufferedBytes?: number;
}

export const LIVE_CLIENT_DEFAULTS = Object.freeze({
  backoffMs: [500, 1000, 2000, 4000] as readonly number[],
  holdWindowMs: 60_000,
  watchdogMs: 15_000,
  pingEveryMs: 10_000,
  maxBufferedBytes: 32_000,
});

const OPEN = 1;

/** Error codes after which reconnecting cannot help (the server also sends retryable: false). */
const TERMINAL_CODES: ReadonlySet<LiveErrorCode> = new Set([
  "bad_token",
  "token_expired",
  "wrong_consult",
  "consult_not_found",
  "consult_ended",
  "already_connected",
]);

export class LiveClient {
  private readonly options: LiveClientOptions;
  private readonly now: () => number;
  private readonly schedule: (fn: () => void, ms: number) => unknown;
  private readonly cancel: (handle: unknown) => void;
  private readonly createSocket: WebSocketFactory;
  private readonly backoff: readonly number[];

  private socket: WebSocketLike | null = null;
  private status: LiveStatus = { kind: "idle" };
  private mode: Mode;
  private everLive = false;
  private terminal = false;
  private attempt = 0;
  private downSince: number | null = null;
  private retryTimer: unknown = null;
  private tickTimer: unknown = null;
  private lastMessageAt = 0;
  private lastPingAt = 0;
  private currentTurn: number | null = null;
  private acceptingAudio = false;
  private pendingEnd: ClientEndReason | null = null;

  constructor(options: LiveClientOptions) {
    this.options = options;
    this.mode = options.mode;
    this.now = options.now ?? (() => Date.now());
    this.schedule = options.schedule ?? ((fn, ms) => setTimeout(fn, ms));
    this.cancel = options.cancel ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
    this.createSocket =
      options.createSocket ?? ((url, protocols) => new WebSocket(url, protocols) as unknown as WebSocketLike);
    this.backoff = options.backoffMs ?? LIVE_CLIENT_DEFAULTS.backoffMs;
    if (options.resumeFrom) {
      // A reload of a call already under way: treat it as a drop that started gapMs ago.
      this.everLive = true;
      this.downSince = this.now() - Math.max(0, options.resumeFrom.gapMs);
    }
  }

  get currentStatus(): LiveStatus {
    return this.status;
  }

  get isLive(): boolean {
    return this.status.kind === "live";
  }

  /** Open the first connection. */
  start(): void {
    if (this.terminal || this.socket) return;
    this.open();
    this.startTicker();
  }

  /** A tap after the client gave up ("lost"), or the page coming back: try at once. */
  reconnectNow(): void {
    if (this.terminal) return;
    if (this.socket && this.socket.readyState === OPEN && this.status.kind === "live") return;
    this.clearRetry();
    if (this.downSince === null) this.downSince = this.now();
    // A fresh hold window: the owner is here and asking.
    this.attempt = 0;
    this.dropSocket();
    this.open();
    this.startTicker();
  }

  /**
   * The page came back from hidden: a socket that has been silent this long is
   * not trusted even when it says OPEN (a phone lock or Wi-Fi to cellular can
   * leave a dead socket that still reports OPEN).
   */
  checkStale(staleMs: number): void {
    if (this.terminal || this.status.kind !== "live") return;
    if (this.now() - this.lastMessageAt >= staleMs) this.handleDrop("stale");
  }

  sendAudio(pcm: ArrayBuffer): boolean {
    const socket = this.socket;
    if (!socket || socket.readyState !== OPEN || this.status.kind !== "live") return false;
    if (!isAcceptableMicMessage(pcm.byteLength)) return false;
    if (socket.bufferedAmount > (this.options.maxBufferedBytes ?? LIVE_CLIENT_DEFAULTS.maxBufferedBytes)) return false;
    socket.send(pcm);
    return true;
  }

  setMode(mode: Mode): void {
    this.mode = mode;
    this.sendEvent({ type: "mode", mode });
  }

  answerExtension(accept: boolean): boolean {
    return this.sendEvent({ type: "extension_answer", accept });
  }

  sendTelemetry(event: Omit<Extract<ClientEvent, { type: "telemetry" }>, "type">): boolean {
    return this.sendEvent({ type: "telemetry", ...event });
  }

  /**
   * Ask to end. The server decides the final reason and ends after Otto's last
   * word; `ended` arrives later. When the socket is down, the request waits for
   * the next `ready`.
   */
  end(reason: ClientEndReason): void {
    if (this.terminal) return;
    if (!this.sendEvent({ type: "end", reason })) this.pendingEnd = reason;
  }

  /** Stop everything (page unload or unmount). No reconnect after this. */
  close(): void {
    this.terminal = true;
    this.clearRetry();
    this.stopTicker();
    this.dropSocket();
  }

  // -------------------------------------------------------------------------

  private setStatus(status: LiveStatus) {
    this.status = status;
    this.options.handlers.onStatus(status);
  }

  private sendEvent(event: ClientEvent): boolean {
    const socket = this.socket;
    if (!socket || socket.readyState !== OPEN || this.status.kind !== "live") return false;
    socket.send(encodeEvent(event));
    return true;
  }

  private open() {
    this.attempt += 1;
    const reconnect = this.everLive;
    this.setStatus({ kind: "connecting", attempt: this.attempt, reconnect });
    let socket: WebSocketLike;
    try {
      // The token rides as a second subprotocol (`otto-token.<token>`), so otto-live
      // checks it before any Durable Object wakes; the server echoes only the version (LIVE_SUBPROTOCOL).
      socket = this.createSocket(this.options.url, liveProtocols(this.options.liveToken));
    } catch (error) {
      // A malformed URL or a blocked scheme: loud, and not something a retry fixes.
      this.fail("socket", error instanceof Error ? error.message : "Could not open the call connection.");
      return;
    }
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    this.currentTurn = null;
    this.acceptingAudio = false;

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.lastMessageAt = this.now();
      const first: ClientEvent = reconnect
        ? {
            type: "resume",
            v: PROTOCOL_VERSION,
            consult_id: this.options.consultId,
            live_token: this.options.liveToken,
            mode: this.mode,
            gap_ms: Math.max(0, Math.round(this.now() - (this.downSince ?? this.now()))),
            last_turn_played: this.options.lastTurnPlayed(),
            ...(this.options.client ? { client: this.options.client } : {}),
          }
        : {
            type: "hello",
            v: PROTOCOL_VERSION,
            consult_id: this.options.consultId,
            live_token: this.options.liveToken,
            mode: this.mode,
            ...(this.options.client ? { client: this.options.client } : {}),
          };
      socket.send(encodeEvent(first));
    };

    socket.onmessage = (message) => {
      if (this.socket !== socket) return;
      this.lastMessageAt = this.now();
      const data = message.data;
      if (data instanceof ArrayBuffer) {
        if (this.acceptingAudio && this.currentTurn !== null && data.byteLength > 0) {
          this.options.handlers.onAudio(data, this.currentTurn);
        }
        return;
      }
      if (typeof data !== "string") return;
      const parsed = parseServerEvent(data);
      if (!parsed.ok) {
        this.options.handlers.onProtocolError?.(parsed.error);
        return;
      }
      this.handleEvent(parsed.event);
    };

    socket.onerror = () => {
      // Browsers fire error then close; the close handler decides what happens.
    };

    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.terminal) return;
      this.handleDrop("closed");
    };
  }

  private handleEvent(event: ServerEvent) {
    switch (event.type) {
      case "ready": {
        this.everLive = true;
        this.attempt = 0;
        this.downSince = null;
        this.setStatus({ kind: "live", resumed: event.resumed });
        break;
      }
      case "otto_speaking":
        this.currentTurn = event.turn;
        this.acceptingAudio = true;
        break;
      case "otto_done":
        if (event.turn === this.currentTurn) this.acceptingAudio = false;
        break;
      case "interrupted":
        if (event.turn === this.currentTurn) this.acceptingAudio = false;
        break;
      case "pong":
        this.options.handlers.onRtt?.(Math.max(0, this.now() - event.t));
        break;
      case "ended":
        this.terminal = true;
        this.acceptingAudio = false;
        this.clearRetry();
        this.stopTicker();
        this.options.handlers.onEvent(event);
        this.setStatus({ kind: "ended", reason: event.reason });
        return;
      case "error":
        if (!event.retryable || TERMINAL_CODES.has(event.code)) {
          this.options.handlers.onEvent(event);
          this.fail(event.code, event.message);
          return;
        }
        break;
      default:
        break;
    }
    this.options.handlers.onEvent(event);
    if (event.type === "ready" && this.pendingEnd) {
      const reason = this.pendingEnd;
      this.pendingEnd = null;
      this.sendEvent({ type: "end", reason });
    }
  }

  private fail(code: LiveErrorCode | "socket", message: string) {
    this.terminal = true;
    this.clearRetry();
    this.stopTicker();
    this.dropSocket();
    this.setStatus({ kind: "failed", code, message });
  }

  private handleDrop(_why: "closed" | "stale" | "watchdog") {
    this.dropSocket();
    this.acceptingAudio = false;
    this.currentTurn = null;
    const now = this.now();
    if (this.downSince === null) this.downSince = now;
    const hold = this.options.holdWindowMs ?? LIVE_CLIENT_DEFAULTS.holdWindowMs;
    if (now - this.downSince >= hold) {
      this.setStatus({ kind: "lost", downSince: this.downSince });
      return;
    }
    // `attempt` counts the connections opened since the line was last live, so
    // the first retry after a drop waits 0.5 s, the next 1 s, then 2 s, then 4 s.
    const index = Math.min(this.attempt, this.backoff.length - 1);
    const delay = this.backoff[index] ?? 4000;
    this.setStatus({ kind: "reconnecting", attempt: this.attempt, nextInMs: delay, downSince: this.downSince });
    this.clearRetry();
    this.retryTimer = this.schedule(() => {
      this.retryTimer = null;
      if (this.terminal) return;
      this.open();
    }, delay);
  }

  private dropSocket() {
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    try {
      socket.close(1000, "client closing");
    } catch {
      // Closing an already closed socket can throw in some browsers; nothing to do.
    }
  }

  private clearRetry() {
    if (this.retryTimer !== null) this.cancel(this.retryTimer);
    this.retryTimer = null;
  }

  // One timer drives the watchdog and the ping: every second, cheap.
  private startTicker() {
    if (this.tickTimer !== null) return;
    const tick = () => {
      this.tickTimer = null;
      if (this.terminal) return;
      const now = this.now();
      if (this.status.kind === "live") {
        const watchdog = this.options.watchdogMs ?? LIVE_CLIENT_DEFAULTS.watchdogMs;
        if (now - this.lastMessageAt >= watchdog) {
          this.handleDrop("watchdog");
        } else if (now - this.lastPingAt >= (this.options.pingEveryMs ?? LIVE_CLIENT_DEFAULTS.pingEveryMs)) {
          this.lastPingAt = now;
          this.sendEvent({ type: "ping", t: now });
        }
      }
      this.tickTimer = this.schedule(tick, 1000);
    };
    this.tickTimer = this.schedule(tick, 1000);
  }

  private stopTicker() {
    if (this.tickTimer !== null) this.cancel(this.tickTimer);
    this.tickTimer = null;
  }
}

/** The live socket URL for a consult, on the page's own host (otto-live is routed at /live/*). */
/**
 * The live socket URL: on otto-live's own origin when the server named one
 * (CheckinResponse.live_origin), else on this page's host (a Workers route or
 * the dev server sends /live/* to otto-live).
 */
export function liveSocketUrl(livePath: string, location: { protocol: string; host: string }, liveOrigin: string | null = null): string {
  if (liveOrigin) {
    const origin = new URL(liveOrigin);
    const scheme = origin.protocol === "https:" ? "wss:" : "ws:";
    return `${scheme}//${origin.host}${livePath}`;
  }
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}${livePath}`;
}
